import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useGraphToken } from "../auth/useGraphToken";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useIsChampion } from "../hooks/useIsChampion";
import { canSee, defaultConfig, fetchNavConfig, readCachedConfig, type NavConfig, type NavEntry, type Viewer } from "./navConfig";

type NavState = {
  config: NavConfig;
  /** False until the saved navigation has loaded (or failed to, leaving the defaults). */
  ready: boolean;
  viewer: Viewer;
  /** The sidebar, for the signed-in person. */
  menu: NavEntry[];
  /** Whether the signed-in person may open an app page. Pages the navigation doesn't manage are open. */
  canOpen: (path: string) => boolean;
  /** Called by the editor after a save, so the sidebar updates straight away. */
  replace: (config: NavConfig) => void;
};

const NavContext = createContext<NavState | null>(null);

export function NavProvider({ children }: { children: ReactNode }) {
  const getToken = useGraphToken();
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const isAdmin = useIsAdmin();
  const isChampion = useIsChampion();
  const cached = useMemo(readCachedConfig, []);
  const [config, setConfig] = useState<NavConfig>(() => cached ?? defaultConfig());
  // The cached copy draws the sidebar at once; pages wait for the fresh one before turning anyone away.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const next = await fetchNavConfig(await getToken());
        if (alive) setConfig(next);
      } catch (err) {
        console.warn("Navigation settings didn't load; using the last known ones.", err);
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [getToken]);

  const viewer = useMemo<Viewer>(
    () => ({ email: (account?.username ?? "").toLowerCase(), isAdmin, isChampion }),
    [account?.username, isAdmin, isChampion]
  );

  const value = useMemo<NavState>(() => {
    const visible = config.filter((e) => canSee(e, viewer));
    return {
      config,
      ready,
      viewer,
      menu: visible.filter((e) => e.onMenu),
      canOpen: (path) => {
        const entry = config.find((e) => e.path === path);
        return !entry || canSee(entry, viewer);
      },
      replace: setConfig,
    };
  }, [config, ready, viewer]);

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNav(): NavState {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error("useNav must be used inside NavProvider.");
  return ctx;
}

/** Opens the page for the people the navigation lets see it; sends everyone else to the dashboard. */
export function PageGuard({ path, children }: { path: string; children: ReactNode }) {
  const { ready, canOpen } = useNav();
  // Wait for the saved navigation before turning anyone away, so someone
  // given access by name isn't bounced on a fresh load.
  if (!canOpen(path)) return ready ? <Navigate to="/" replace /> : null;
  return <>{children}</>;
}
